import { Button, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { deDE, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { ILegalTransactionRelationshipMember } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipMember';
import { randomNumber } from '../../../../../utils';
import { DialogResponsive } from '../../../../core/DialogResponsive';
import { CrudDataGrid } from '../../generic/CrudDataGrid';
import { useLegalClientStore } from '../../generic/legalClientStore';
import { NavRow } from '../../generic/NavRow';

export type ILegalClientAssetProvision = {
  id: number;
  idLegalClient: number;
  provisionNote: string;
};

export type IAssetProvisions = {
  legalClientProvisions: ILegalClientAssetProvision[];
  otherProvisionsNotes?: string;
};

type TestamentLegalClientProvisionDialogProps = {
  id?: number;
  legalClientProvisions: ILegalClientAssetProvision[];
  testatorArray: ILegalTransactionRelationshipMember[];
  onClose: () => void;
  onSave: (legalClientAssetProvision: ILegalClientAssetProvision) => void;
};

function TestamentLegalClientProvisionDialog({
  id,
  legalClientProvisions,
  testatorArray,
  onClose,
  onSave,
}: TestamentLegalClientProvisionDialogProps) {
  const [legalClientArray] = useLegalClientStore(state => [
    state.legalClientArray,
  ]);

  const [provisionNote, setProvisionNote] = useState<string>('');
  const [idLegalClient, setIdLegalClientId] = useState<number>(0);

  useEffect(() => {
    if (id) {
      const provision = legalClientProvisions.find(p => p.id === id);
      if (provision) {
        setIdLegalClientId(provision.idLegalClient);
        setProvisionNote(provision.provisionNote);
      }
    }
  }, [id, legalClientProvisions]);

  return (
    <DialogResponsive
      open={true}
      onClose={onClose}
      maxWidth='md'
      title='Bestimmung für Begünstigten hinzufügen'
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <TextField
            label='Begünstigter'
            select
            value={idLegalClient ?? ''}
            onChange={e => setIdLegalClientId(Number(e.target.value))}
            size='small'
            fullWidth
          >
            {legalClientArray
              .filter(
                lc =>
                  !legalClientProvisions
                    .map(({ idLegalClient }) => idLegalClient)
                    .includes(lc.idLegalClient) &&
                  !testatorArray
                    .map(t => t.idLegalClient)
                    .includes(lc.idLegalClient)
              )
              .map(lc => (
                <MenuItem key={lc.idLegalClient} value={lc.idLegalClient}>
                  {lc.FirstName} {lc.LastName}
                </MenuItem>
              ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label='Bestimmungsnotiz'
            type='text'
            multiline
            value={provisionNote}
            onChange={e => setProvisionNote(e.target.value)}
            size='small'
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <NavRow
            nextButtonText='Speichern'
            handleNext={() => {
              if (idLegalClient) {
                onSave({
                  id: id || randomNumber(),
                  idLegalClient,
                  provisionNote,
                });
              }
            }}
            backButtonText='Abbrechen'
            handleBack={onClose}
          />
        </Grid>
      </Grid>
    </DialogResponsive>
  );
}

type TestamentProvisionsViewProps = {
  testatorArray: ILegalTransactionRelationshipMember[];
  provisions: IAssetProvisions;
  onProvisionsChange: (provisions: IAssetProvisions) => void;
};

export function TestamentProvisionsView({
  testatorArray,
  provisions,
  onProvisionsChange,
}: TestamentProvisionsViewProps) {
  const [getLegalClient, legalClientArray] = useLegalClientStore(state => [
    state.getLegalClient,
    state.legalClientArray,
  ]);

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [currentLegalClientProvisionId, setCurrentLegalClientProvisionId] =
    useState<number | undefined>(undefined);

  const onClose = () => {
    setOpenDialog(false);
  };

  const columns: GridColDef<ILegalClientAssetProvision, any, any>[] = [
    {
      field: 'legalClientName',
      headerName: 'Begünstigter',
      valueGetter: (
        params: GridValueGetterParams<ILegalClientAssetProvision, any>
      ) => {
        const legalClient = getLegalClient(params.row.idLegalClient);
        return legalClient
          ? `${legalClient.FirstName} ${legalClient.LastName}`
          : 'Unbekannt';
      },
      width: 300,
    },
    {
      field: 'provisionNote',
      type: 'text',
      headerName: 'Bestimmungsnotiz',
      flex: 1,
      headerAlign: 'left',
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Typography variant='h4'>Bestimmungen für dieses Vermögen</Typography>
      </Grid>
      <Grid item xs={6}>
        <Button
          sx={{ float: 'right' }}
          variant='contained'
          onClick={() => {
            setCurrentLegalClientProvisionId(undefined);
            setOpenDialog(true);
          }}
        >
          Bestimmung für Begünstigten hinzufügen
        </Button>
      </Grid>
      <Grid item xs={12}>
        <CrudDataGrid
          sx={{ mt: 2 }}
          autoHeight
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          rows={provisions.legalClientProvisions}
          columns={columns}
          getRowId={row => row.id}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          pageSizeOptions={[5]}
          disableRowSelectionOnClick
          onClickEdit={idLegalClientProvision => {
            setCurrentLegalClientProvisionId(Number(idLegalClientProvision));
            setOpenDialog(true);
          }}
          onClickDelete={idLegalClientProvision => {
            onProvisionsChange({
              ...provisions,
              legalClientProvisions: provisions.legalClientProvisions.filter(
                provision =>
                  provision.idLegalClient !== Number(idLegalClientProvision)
              ),
            });
          }}
        />
      </Grid>

      {openDialog && (
        <TestamentLegalClientProvisionDialog
          id={currentLegalClientProvisionId}
          testatorArray={testatorArray}
          onClose={onClose}
          legalClientProvisions={provisions.legalClientProvisions}
          onSave={legalClientAssetProvision => {
            if (
              !provisions.legalClientProvisions.find(
                p => p.id === legalClientAssetProvision.id
              )
            ) {
              onProvisionsChange({
                ...provisions,
                legalClientProvisions: [
                  ...provisions.legalClientProvisions,
                  legalClientAssetProvision,
                ],
              });
              onClose();
              return;
            }
            onProvisionsChange({
              ...provisions,
              legalClientProvisions: provisions.legalClientProvisions.map(
                provision =>
                  provision.id === legalClientAssetProvision.id
                    ? legalClientAssetProvision
                    : provision
              ),
            });
            onClose();
          }}
        />
      )}

      <Grid item xs={12}>
        <TextField
          label='Sonstige Bestimmungen'
          multiline
          fullWidth
          minRows={3}
          value={provisions.otherProvisionsNotes || ''}
          onChange={e => {
            console.log(e.target.value);
            onProvisionsChange({
              ...provisions,
              otherProvisionsNotes: e.target.value,
            });
          }}
        />
      </Grid>
    </Grid>
  );
}
